<script setup lang="ts" generic="KIND extends string">
import { useElementBounding } from "@vueuse/core";
import { delay } from "es-toolkit";
import Highcharts from "highcharts";
import { computed, ref, useTemplateRef, watch } from "vue";

import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { buildColumnChartOptions, IColumnChartSpecs } from "@/utils/chart/column-chart-options-utils";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseChart from "@/components/ui/chart/base-chart.vue";

interface IProps {
  columnChartSpecs?: IColumnChartSpecs<KIND>;
  series?: TCategorizedDataResponseSerie<KIND>[];
  navigable?: boolean;
  isLoading?: boolean;
  hasNoData?: boolean;
}

const props = defineProps<IProps>();

const maxSeriesLength = computed<number>(() => {
  if (!props.series) return 0;

  let maxLength = 0;

  props.series.forEach((serie) => {
    const columnSerie = serie;
    const columnSerieDataLength = columnSerie.value.length ?? 0;
    if (columnSerieDataLength > maxLength) {
      maxLength = columnSerieDataLength;
    }
  });

  return maxLength;
});

const STEP = 9;
const minXAxisIndex = ref<number>(0);
const maxXAxisIndex = ref<number>(STEP);

const { t, k } = useTypedIntl();

const noDataMessageContainer = useTemplateRef("noDataMessageContainer");
const { height: noDataMessageContainerHeight, width: noDataMessageContainerWidth } =
  useElementBounding(noDataMessageContainer);
const chartContainer = useTemplateRef("chartContainer");
const { height: chartContainerHeight, width: chartContainerWidth } = useElementBounding(chartContainer);

const canNavigateLeft = computed<boolean>(() => {
  return props.navigable ? minXAxisIndex.value > 0 : false;
});

const canNavigateRight = computed<boolean>(() => {
  return props.navigable ? maxXAxisIndex.value < maxSeriesLength.value - 1 : false;
});

const columnChartOpts = computed<Highcharts.Options | undefined>(() => {
  if (props.isLoading || !props.series || !props.columnChartSpecs) {
    return;
  }

  const defaultOpts = buildColumnChartOptions(props.columnChartSpecs, props.series);

  return {
    ...defaultOpts,
    xAxis: {
      ...defaultOpts.xAxis,
      min: props.navigable ? minXAxisIndex.value : undefined,
      max: props.navigable ? maxXAxisIndex.value : undefined,
    },
  };
});

async function fadeChartLabelOut() {
  const labels = document.querySelectorAll(".base-column-chart .highcharts-label>text");

  labels.forEach((label) => {
    label.classList.add("opacity-0");
  });

  await delay(400);

  labels.forEach((label) => {
    label.classList.remove("opacity-0");
  });
}

function slide(direction?: "right" | "left") {
  // Fire and forget (make the labels fade out while the chart is updating)
  fadeChartLabelOut();

  const atUpperBound = maxXAxisIndex.value + STEP > maxSeriesLength.value - 1;
  const atLowerBound = minXAxisIndex.value - STEP < 0;

  if (direction === "right") {
    if (atUpperBound) {
      maxXAxisIndex.value = maxSeriesLength.value - 1;
      minXAxisIndex.value = maxXAxisIndex.value - STEP;
    } else {
      minXAxisIndex.value += STEP;
      maxXAxisIndex.value += STEP;
    }
  } else if (atLowerBound) {
    minXAxisIndex.value = 0;
    maxXAxisIndex.value = STEP;
  } else {
    minXAxisIndex.value -= STEP;
    maxXAxisIndex.value -= STEP;
  }
}

watch(
  maxSeriesLength,
  (newValue) => {
    maxXAxisIndex.value = newValue > STEP ? STEP : newValue - 1;
  },
  { immediate: true },
);
</script>

<template>
  <div
    ref="chartContainer"
    class="relative"
  >
    <div
      :class="{
        'pointer-events-none blur-[5px]': !props.isLoading && props.hasNoData,
      }"
    >
      <div
        ref="base-column-chart-container"
        class="relative base-column-chart"
      >
        <BaseChart
          ref="chart"
          :is-loading="props.isLoading"
          :options="columnChartOpts"
        />

        <HospitalityButton
          v-show="navigable && canNavigateLeft"
          class="!absolute !left-[40px] !top-[150px]"
          is-icon-only
          severity="tertiary"
          size="xs"
          @click="slide('left')"
        >
          <i class="pi pi-arrow-left" />
        </HospitalityButton>
        <HospitalityButton
          v-show="navigable && canNavigateRight"
          class="!absolute !right-0 !top-[150px] hover:!bg-blue-300/20"
          is-icon-only
          severity="tertiary"
          size="xs"
          @click="slide('right')"
        />
      </div>
    </div>

    <div
      v-show="!props.isLoading && props.hasNoData"
      ref="noDataMessageContainer"
      class="border p-4 rounded-md flex flex-col gap-2 absolute w-[440px] bg-white"
      :style="{
        top: `${Math.floor(chartContainerHeight / 2 - noDataMessageContainerHeight / 2)}px`,
        left: `${Math.floor(chartContainerWidth / 2 - noDataMessageContainerWidth / 2)}px`,
      }"
    >
      <slot name="noDataMessage">
        <div class="flex justify-center">
          <h5>{{ t(k.global.noData) }}</h5>
        </div>
      </slot>
    </div>
  </div>
</template>
